import { useEffect, useRef } from "react";
import { CheckButton, HollowedButton, NormalButton } from "@shared/designSystem/Button";
import ColorInput from "@shared/designSystem/ColorInput";
import { ColumnsContainer, HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import DetailField from "@shared/designSystem/DetailField";
import { Modal } from "@shared/designSystem/messages/Modal";
import { TextInput, TextInputRef } from "@shared/designSystem/TextInput";
import { SmallText, Text } from "@shared/designSystem/Texts";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { CreateNovedadRequest, Novedad } from "@services/NovedadesService";
import { useNovedadEditModalViewModel } from "./useNovedadEditModalViewModel";

interface NovedadEditModalProps {
    novedad: Novedad | null;
    isOpen: boolean;
    loading: boolean;
    onClose: () => void;
    onSave: (novedad: Novedad, payload: CreateNovedadRequest, isUsableInLicenses: boolean) => Promise<void>;
}

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <VerticalContainer width="100%" gap={designSystemStyles.gapMd}>
        <Text text={`**${title.trim()}**`} markdown={true} color={designSystemStyles.colorText} />
        <VerticalContainer
            width="100%"
            gap={designSystemStyles.gapMd}
            backgroundColor={designSystemStyles.colorBackgroundGray2}
            borderRadius={designSystemStyles.cornerRadiusComponent}
            padding={designSystemStyles.paddingMd}
        >
            {children}
        </VerticalContainer>
    </VerticalContainer>
);

const ColorPreviewCard: React.FC<{ label: string; backgroundColor?: string; foregroundColor?: string }> = ({
    label,
    backgroundColor,
    foregroundColor,
}) => (
    <VerticalContainer
        width="100%"
        gap={designSystemStyles.gapSm}
        backgroundColor={backgroundColor || designSystemStyles.colorBackgroundWhite}
        borderRadius={designSystemStyles.cornerRadiusComponent}
        padding={designSystemStyles.paddingMd}
    >
        <SmallText text={label} color={foregroundColor || designSystemStyles.colorTextSecondary} />
        <Text text={"Vista previa".localized()} color={foregroundColor || designSystemStyles.colorText} />
    </VerticalContainer>
);

const NovedadEditModal: React.FC<NovedadEditModalProps> = ({
    novedad,
    isOpen,
    loading,
    onClose,
    onSave,
}) => {
    const descriptionInputRef = useRef<TextInputRef>(null);
    const {
        formState,
        errors,
        canSave,
        payload,
        setName,
        setPayrollCode,
        setBackgroundColor,
        setForegroundColor,
        setIsUsableInLicenses,
    } = useNovedadEditModalViewModel(novedad, isOpen);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const timeout = window.setTimeout(() => {
            descriptionInputRef.current?.focus();
        }, 0);

        return () => window.clearTimeout(timeout);
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={"Editar novedad".localized()}
            width="760px"
            height="75vh"
            maxWidth="96vw"
        >
            <VerticalContainer width="100%" height="100%" gap={designSystemStyles.gapLg}>
                {novedad ? (
                    <>
                        <DetailSection title={"Resumen".localized()}>
                            <ColumnsContainer minColumnWidth="180px" gap={designSystemStyles.gapLg}>
                                <DetailField label={"Código".localized()} value={novedad.acronym} />
                                <DetailField label={"Descripción actual".localized()} value={novedad.name} />
                                <DetailField
                                    label={"WhatsApp".localized()}
                                    value={formState.isUsableInLicenses ? "Habilitado".localized() : "Deshabilitado".localized()}
                                />
                            </ColumnsContainer>
                        </DetailSection>

                        <div style={{ width: "100%", flex: 1, minHeight: 0, overflowY: "auto" }}>
                            <VerticalContainer
                                width="100%"
                                gap={designSystemStyles.gapLg}
                                paddingBottom={designSystemStyles.paddingSm}
                            >
                                <DetailSection title={"WhatsApp".localized()}>
                                    <HorizontalContainer justifyContent="flex-start" width="100%">
                                        <CheckButton
                                            text={"Habilitado para WhatsApp".localized()}
                                            selected={formState.isUsableInLicenses}
                                            onClick={() => setIsUsableInLicenses(!formState.isUsableInLicenses)}
                                            disabled={loading}
                                        />
                                    </HorizontalContainer>
                                </DetailSection>

                                <DetailSection title={"Datos editables".localized()}>
                                    <ColumnsContainer minColumnWidth="220px" gap={designSystemStyles.gapLg}>
                                        <TextInput
                                            ref={descriptionInputRef}
                                            label={"Descripción".localized()}
                                            value={formState.name}
                                            onChange={setName}
                                            placeholder={"Ingresá la descripción".localized()}
                                            error={errors.name}
                                            disabled={loading}
                                        />
                                        <TextInput
                                            label={"Código de sistema liquidación".localized()}
                                            value={formState.payrollCode}
                                            onChange={setPayrollCode}
                                            placeholder={"Ej. VAC-001".localized()}
                                            disabled={loading}
                                        />
                                        <ColorInput
                                            label={"Color de fondo".localized()}
                                            value={formState.backgroundColor}
                                            onChange={setBackgroundColor}
                                            placeholder={"Ej. #EC6C56".localized()}
                                            error={errors.backgroundColor}
                                            disabled={loading}
                                            width="100%"
                                            fallbackValue={novedad.backgroundColor || "#EC6C56"}
                                        />
                                        <ColorInput
                                            label={"Color de texto".localized()}
                                            value={formState.foregroundColor}
                                            onChange={setForegroundColor}
                                            placeholder={"Ej. #FFFFFF".localized()}
                                            error={errors.foregroundColor}
                                            disabled={loading}
                                            width="100%"
                                            fallbackValue={novedad.foregroundColor || "#FFFFFF"}
                                        />
                                    </ColumnsContainer>
                                </DetailSection>

                                <DetailSection title={"Vista previa".localized()}>
                                    <ColumnsContainer minColumnWidth="220px" gap={designSystemStyles.gapLg}>
                                        <ColorPreviewCard
                                            label={"Color configurado".localized()}
                                            backgroundColor={formState.backgroundColor || undefined}
                                            foregroundColor={formState.foregroundColor || undefined}
                                        />
                                        <VerticalContainer width="100%" gap={designSystemStyles.gapSm}>
                                            <SmallText
                                                text={"Código".localized()}
                                                color={designSystemStyles.colorTextSecondary}
                                            />
                                            <Text text={novedad.acronym} />
                                            <SmallText
                                                text={"Descripción".localized()}
                                                color={designSystemStyles.colorTextSecondary}
                                            />
                                            <Text text={formState.name || "Sin descripción".localized()} />
                                        </VerticalContainer>
                                    </ColumnsContainer>
                                </DetailSection>
                            </VerticalContainer>
                        </div>

                        <HorizontalContainer justifyContent="flex-end" width="100%" gap={designSystemStyles.gapMd}>
                            <HollowedButton
                                text={"Cancelar".localized()}
                                onClick={onClose}
                                disabled={loading}
                            />
                            <NormalButton
                                text={"Guardar".localized()}
                                onClick={() => {
                                    if (!novedad || !payload || !canSave) {
                                        return;
                                    }
                                    void onSave(novedad, payload, formState.isUsableInLicenses);
                                }}
                                disabled={!canSave}
                                loading={loading}
                            />
                        </HorizontalContainer>
                    </>
                ) : null}
            </VerticalContainer>
        </Modal>
    );
};

export default NovedadEditModal;
